import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/api/dashboard.api';
import { useGetCompanies } from '@/queries/company.queries';
import { useCompanyStore } from '@/store/useCompanyStore';

/** Convert "YYYY-MM" string to { year, month } API params */
export function parseMonthParam(month: string): { year: number; month: number } {
  const [year, m] = month.split('-').map(Number);
  return { year, month: m };
}

/** Resolves the active company ID from the store + companies list. */
export const useActiveCompanyId = (): string | undefined => {
  const { data: companies = [] } = useGetCompanies();
  const { activeCompanyId } = useCompanyStore();

  const resolved =
    companies.find((c) => c.id === activeCompanyId) ??
    companies.find((c) => !c.isDemo) ??
    companies[0];

  return resolved?.id;
};

export const dashboardKeys = {
  all: () => ['dashboard'] as const,
  cashTrends: (companyId: string, month: string) => [...dashboardKeys.all(), 'cash-trends', companyId, month] as const,
  profitSpend: (companyId: string, month: string) => [...dashboardKeys.all(), 'profit-spend', companyId, month] as const,
  moneyInOut: (companyId: string, month: string) => [...dashboardKeys.all(), 'money-in-out', companyId, month] as const,
  customerSales: (companyId: string, month: string) => [...dashboardKeys.all(), 'customer-sales', companyId, month] as const,
};

export const useDashboardCashTrends = (month: string) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: dashboardKeys.cashTrends(companyId ?? '', month),
    queryFn: () => dashboardApi.getCashTrends(companyId!, parseMonthParam(month)),
    enabled: !!month && !!companyId,
  });
};

export const useDashboardProfitSpend = (month: string) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: dashboardKeys.profitSpend(companyId ?? '', month),
    queryFn: () => dashboardApi.getProfitSpend(companyId!, parseMonthParam(month)),
    enabled: !!month && !!companyId,
  });
};

export const useDashboardMoneyInOut = (month: string) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: dashboardKeys.moneyInOut(companyId ?? '', month),
    queryFn: () => dashboardApi.getMoneyInOut(companyId!, parseMonthParam(month)),
    enabled: !!month && !!companyId,
  });
};

export const useDashboardCustomerSales = (month: string) => {
  const companyId = useActiveCompanyId();
  return useQuery({
    queryKey: dashboardKeys.customerSales(companyId ?? '', month),
    queryFn: () => dashboardApi.getCustomerSales(companyId!, parseMonthParam(month)),
    enabled: !!month && !!companyId,
  });
};
